import React, { useCallback, useEffect, useState } from 'react';
import {
  useDevices,
  useDaily,
  useDailyEvent,
  DailyVideo,
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from '../../shadcn/alert-dialog';

import { Alert, AlertTitle, AlertDescription } from '../../shadcn/alert';
import UserMediaError from '../UserMediaError/UserMediaError';
import { useAuth } from '../../../contexts/AuthContext';

import './HairCheck.css';

export default function HairCheck({
  joinCall,
  cancelCall,
  joinStatusMessage = '',
  isRefreshingAssignment = false,
}) {

  const { user } = useAuth();
  const localSessionId = useLocalSessionId();
  const initialUsername = useParticipantProperty(localSessionId, 'user_name');
  const { currentCam, currentMic, currentSpeaker, microphones, speakers, cameras, setMicrophone, setCamera, setSpeaker } = useDevices();
  const callObject = useDaily();
  const [username, setUsername] = useState(initialUsername);
  const [displayName, setDisplayName] = useState('');

  const [getUserMediaError, setGetUserMediaError] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState('');

  useEffect(() => {
    //setUsername(initialUsername);
    setUsername(user.email);
  }, [initialUsername]);

  useDailyEvent(
    'camera-error',
    useCallback(() => {
      setGetUserMediaError(true);
    }, []),
  );

  const handleChange = (e) => {
    setUsername(e.target.value);
    callObject.setUserName(e.target.value);
  };

  const handleDisplayNameChange = (e) => {
    setDisplayName(e.target.value);
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setShowDialog(true);
      return;
    }
    setJoinError('');
    setIsJoining(true);
    try {
      await joinCall(username.trim(), displayName.trim());
    } catch (error) {
      const serverMessage = error?.response?.data?.message;
      setJoinError(
        serverMessage ||
          error?.message ||
          'Unable to join call right now. Please try again.',
      );
    } finally {
      setIsJoining(false);
    }
  };

  const updateMicrophone = (e) => {
    setMicrophone(e.target.value);
  };

  const updateSpeakers = (e) => {
    setSpeaker(e.target.value);
  };

  const updateCamera = (e) => {
    setCamera(e.target.value);
  };

  return getUserMediaError ? (
    <UserMediaError />
  ) : (
    <>
    <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Display Name Required</AlertDialogTitle>
        <AlertDialogDescription>
          Please enter your display name before joining the call. This is the name that other participants will see.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogAction onClick={() => setShowDialog(false)}>
          Okay
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

    <form className="hair-check" onSubmit={handleJoin}>
      <h1>Setup your hardware</h1>

      {/* Display Name */}
      <div className="display-name-field">
        <label htmlFor="displayName">Enter your child's name below</label>
        <input
          id="displayName"
          name="displayName"
          type="text"
          placeholder="Type name here, e.g. Amara"
          onChange={(e) => setDisplayName(e.target.value)}
          value={displayName}
          className={displayName.trim() ? 'has-value' : ''}
          autoFocus
          autoComplete="off"
          required
        />
      </div>

      <button onClick={handleJoin} className="join-call" type="submit" disabled={isJoining || isRefreshingAssignment}>
        {isJoining || isRefreshingAssignment ? 'Joining…' : 'Join call'}
      </button>
      {(joinStatusMessage || joinError) && (
        <Alert className="mt-3">
          <AlertTitle>{joinError ? 'Unable to join yet' : 'Please wait'}</AlertTitle>
          <AlertDescription>{joinError || joinStatusMessage}</AlertDescription>
        </Alert>
      )}

      {/* Video preview */}
      {localSessionId && <DailyVideo sessionId={localSessionId} mirror />}

      {/* Username */}
      <div>
        <label htmlFor="username">Your email:</label>
        <input
          name="username"
          type="text"
          value={username || ''}
          readOnly
          disabled
        />
      </div>

      {/* Microphone select */}
      <div>
        <label htmlFor="micOptions">Microphone:</label>
        <select name="micOptions" id="micSelect" onChange={updateMicrophone} value={currentMic?.device?.deviceId}>
          {microphones.map((mic) => (
            <option key={`mic-${mic.device.deviceId}`} value={mic.device.deviceId}>
              {mic.device.label}
            </option>
          ))}
        </select>
      </div>

      {/* Speakers select */}
      <div>
        <label htmlFor="speakersOptions">Speakers:</label>
        <select name="speakersOptions" id="speakersSelect" onChange={updateSpeakers} value={currentSpeaker?.device?.deviceId}>
          {speakers.map((speaker) => (
            <option key={`speaker-${speaker.device.deviceId}`} value={speaker.device.deviceId}>
              {speaker.device.label}
            </option>
          ))}
        </select>
      </div>

      {/* Camera select */}
      <div>
        <label htmlFor="cameraOptions">Camera:</label>
        <select name="cameraOptions" id="cameraSelect" onChange={updateCamera} value={currentCam?.device?.deviceId}>
          {cameras.map((camera) => (
            <option key={`cam-${camera.device.deviceId}`} value={camera.device.deviceId}>
              {camera.device.label}
            </option>
          ))}
        </select>
      </div>

      <button onClick={cancelCall} className="cancel-call" type="button">
        Back to start
      </button>
    </form>
    </>
  );
}